import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { Menu } from '@grapecity/wijmo.input';
import { ActivatedRoute, Router } from '@angular/router';
import { FlexGrid, CellType } from '@grapecity/wijmo.grid';
import { KYMW00Model } from '../../../commons/kymw00/kymw00.model';
import { DataGridUtil } from '../../../commons/kymw00/data-grid-util';
import { PlessViewHelperSub } from '../../../commons/kymw00/pless-view-helper-sub';
import { PlessDialogServiceSub } from '../../../commons/kymw00/pless-dialog-service-sub';
import { CodeComboBoxComponent } from '../../../commons/kymw02/combo-box/code-combo-box/code-combo-box.component';
import { ClassCodeComboBoxComponent } from '../../../commons/kymw02/combo-box/class-code-combo-box/class-code-combo-box.component';
import { PlessAlert } from '../../../shared/pless-alert/pless-alert.component';
import { KYMW7400Model, REGISTER_PAGE_ID } from './kymw7400.model';
import { ConfirmRememberGoodsService } from './api-service/confirm-remember-goods.service';
import { GetRememberGoodsListService } from './api-service/get-remember-goods-list.service';
import { DeleteRememberGoodsListService } from './api-service/delete-remember-goods-list.service';
import { KYMW7401Component } from '../kymw7401/kymw7401.component';
import { KYMW7403Component } from '../kymw7403/kymw7403.component';

import type { RememberGoodsInfo } from './vo/remember-goods-info.model';
import type { DeleteRememberGoodsListParameter } from './vo/delete-remember-goods-list-combination.model';
import type { KYMW7401OutputModel } from '../kymw7401/vo/kymw7401-output.model';

/** コンテキストメニュー用インターフェース */
export interface ContextMenu {
  /** メニューアイテム表示名 */
  caption: string;
  /** コマンド */
  cmd: string;
  /** 活性フラグ */
  enabled: boolean;
}

/** 忘れず注文商品一覧 画面コンポーネント */
@Component({
  selector: 'app-kymw7400',
  templateUrl: './kymw7400.component.html',
  styleUrls: ['./kymw7400.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class KYMW7400Component extends PlessViewHelperSub implements OnInit, AfterViewInit {
  model = new KYMW7400Model();
  private contextMenu: Menu;

  // 検索済かをチェックするパラメータ
  private searchFlg: boolean = false;

  @ViewChild('cmb_CodeList', { static: true }) cmb_CodeList: CodeComboBoxComponent; // バイヤーコード
  @ViewChild('cmb_ClassCodeList', { static: true }) cmb_ClassCodeList: ClassCodeComboBoxComponent; // 部門
  @ViewChild('dtg_RememberGoodsList', { static: true }) dtg_RememberGoodsList: FlexGrid;

  constructor(
    private router: Router,
    private kymw00Model: KYMW00Model,
    private dialogService: PlessDialogServiceSub,
    private confirmRememberGoodsService: ConfirmRememberGoodsService,
    private getRememberGoodsListService: GetRememberGoodsListService,
    private deleteRememberGoodsListService: DeleteRememberGoodsListService,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
  ) {
    super(kymw00Model);
  }

  ngOnInit() {
    this.componentName = 'KYMW7400';

    // コンテキストメニュー設定
    this.setContextMenu();

    this.route.queryParams.subscribe((params) => {
      // 忘れず注文商品登録画面で保持していた検索フラグ取得
      if('searchFlg' in params) {
        this.searchFlg = params['searchFlg'];
      }
      // 忘れず注文商品登録画面で保持していた検索条件取得
      if ('searchOrderReceptionCatalog' in params) {
        this.model.orderReceptionCatalog = params['searchOrderReceptionCatalog'];
      }
      if ('searchClassCode' in params) {
        this.model.classCode = params['searchClassCode'];
      }
      if ('searchPCode' in params) {
        this.model.pCode = params['searchPCode'];
      }
      if ('searchDeliveryCatalog' in params) {
        this.model.deliveryCatalog = params['searchDeliveryCatalog'];
      }
      if ('searchBuyerCode' in params) {
        this.model.buyerCode = params['searchBuyerCode'];
      }
      if ('searchGuideGoodsName' in params) {
        this.model.guideGoodsName = params['searchGuideGoodsName'];
      }
      if ('searchOrderNum' in params) {
        this.model.orderNum = params['searchOrderNum'];
      }
      if ('searchItemCode' in params) {
        this.model.itemCode = params['searchItemCode'];
      }
      if ('searchGuideStandardName' in params) {
        this.model.guideStandardName = params['searchGuideStandardName'];
      }

      // 遷移前に検索していた場合再検索
      if(this.searchFlg) {
        this.eventSearchButton_CLICKHandler();
      }
    });
  }

  ngAfterViewInit() {
    // チェックボックスのあたり判定制御
    this.dtg_RememberGoodsList.hostElement.addEventListener('click', e => {
      const ht = this.dtg_RememberGoodsList.hitTest(e.pageX, e.pageY);
      if (ht.cellType === CellType.Cell && ht.col === 0) {
        const val = this.dtg_RememberGoodsList.getCellData(ht.row, ht.col, false);
        this.dtg_RememberGoodsList.setCellData(ht.row, ht.col, val ? false : true);
      }
    });
  }

  // 「検索」ボタン押下イベント
  eventSearchButton_CLICKHandler() {
    // 検索ボタン押下時のみリクエスト内容をセットして保持する
    this.model.searchCondition = {
      OrderReceptionCatalog: this.model.orderReceptionCatalog ?? '',
      DeliveryCatalog: this.model.deliveryCatalog ?? '',
      OrderNum: this.model.orderNum ?? '',
      ClassCode: this.model.classCode ?? '',
      BuyerCode: this.model.buyerCode ?? '',
      ItemCode: this.model.itemCode ?? '',
      Pcode: this.model.pCode ?? '',
      GuideGoodsName: this.model.guideGoodsName ?? '',
      GuideStandardName: this.model.guideStandardName ?? '',
      Page: '1',
    }

    // 検索フラグを更新
    this.searchFlg = true;

    this.getRememberGoodsList(1);
  }

  // グリッドページャ「前頁」ボタン押下イベント
  eventPrevPageButton_CLICKHandler() {
    const currentPageNo = this.model.currentPageNo;
    if (currentPageNo > 1) {
      // 現在のページ番号-1を設定し、リストを取得
      this.getRememberGoodsList(currentPageNo - 1);
    }
  }

  // グリッドページャ「次頁」ボタン押下イベント
  eventNextPageButton_CLICKHandler() {
    const currentPageNo = this.model.currentPageNo;
    const endPageNo = this.model.endPageNo;
    if (currentPageNo < endPageNo) {
      // 現在のページ番号+1を設定し、リストを取得
      this.getRememberGoodsList(currentPageNo + 1);
    }
  }

  // 「新規追加」ボタン押下イベント
  eventAddButton_CLICKHandler() {
    // 忘れず注文商品対象選択ダイアログを表示
    const componentRef = this.dialogService.openModal(KYMW7401Component);
    const instance = componentRef.componentInstance as KYMW7401Component;
    instance.parent = this;
    // ダイアログ内「登録商品として使用」実行時処理
    instance.setKYMW7401Parameter = (output: KYMW7401OutputModel) => {
      componentRef.close(); // ダイアログを閉じる
      // 選択された商品のアイテムコードだけを渡して登録画面へ遷移
      const itemCode = output.selectedGoodsInfo.itemCode;
      this.gotoRegisterPage(itemCode);
    };

    // ウィンドウが閉じられる際の処理
    instance.closeFunction = () => {
      componentRef.close();
    };
  }

  // 「削除」ボタン押下イベント
  eventDeleteButton_CLICKHandler() {
    // 選択されたデータを取得
    const selectedItems = this.model.viewRememberGoodsList.filter(item => item.check);

    if (selectedItems.length > 0) {
      // 削除確認メッセージを表示
      this.dialogService.openAlertGetMessage('Q0106', ['忘れず注文商品'], (closeDetail) => {
        // 「はい」を押下した場合、処理実行
        if (closeDetail === PlessAlert.Yes) {
          this.deleteRememberGoodsList(selectedItems);
        }
      });
    } else {
      // 対象未選択のエラーメッセージを表示
      this.dialogService.openAlertGetMessage('E0103', ['忘れず注文商品']);
    }
  }

  // 「情報確定」ボタン押下イベント
  eventConfirmButton_CLICKHandler() {
    // 確認メッセージを表示
    this.dialogService.openAlertGetMessage('Q0152', ['情報確定'], (closeDetail) => {
      // 「はい」を押下した場合、処理実行
      if (closeDetail === PlessAlert.Yes) {
        this.confirmRememberGoods();
      }
    });
  }

  // 「リスト出力」ボタン押下イベント
  eventOutputListButton_CLICKHandler() {
    // 忘れず注文商品リスト出力画面（モーダル）表示
    const componentRef = this.dialogService.openModal(KYMW7403Component);
    const instance = componentRef.componentInstance as KYMW7403Component;
    instance.parent = this;

    // ウィンドウが閉じられる際の処理
    instance.closeFunction = () => {
      componentRef.close();
    };
  }

  // コンテキストメニューを設定
  private setContextMenu(): void {
    // コンテキストメニュー作成 (提案候補商品一覧用)
    this.contextMenu = new Menu(document.createElement('div'), {
      displayMemberPath: 'caption',
      selectedValuePath: 'cmd',
      itemsSource: [
        { caption: '詳細', cmd: 'detail', enabled: false },
      ] as ContextMenu[],
      command: { canExecuteCommand: (item: ContextMenu) => item.enabled }, // 各項目の活性制御
      itemClicked: () => {
        if (this.contextMenu.selectedValue === 'detail') {
          const itemCode = this.model.selectedRememberGoods.itemCode;
          const rememberGoodsId = this.model.selectedRememberGoods.rememberGoodsId;

          // 登録画面に遷移
          this.gotoRegisterPage(itemCode, rememberGoodsId);
        }
      },
    });

    // コンテキストメニュー表示処理 (提案候補商品一覧用)
    this.dtg_RememberGoodsList.hostElement.addEventListener('contextmenu', e => {
      // イベントを停止後、メニュー表示
      e.preventDefault();

      // コンテキストメニューの活性/非活性 制御
      let isEnabled = false;
      if (this.model.viewRememberGoodsList.length > 0) {
        isEnabled = true;
      }
      this.contextMenu.itemsSource.forEach((item: ContextMenu) => {
        item.enabled = isEnabled;
      });

      // コンテキストメニュー表示
      DataGridUtil.showContextMenu(this.dtg_RememberGoodsList, this.contextMenu, e);

      // 選択行のインデックスを取得
      const ht = this.dtg_RememberGoodsList.hitTest(e.pageX, e.pageY);
      const selectRowIndex = ht.row;
      if (selectRowIndex >= 0) {
        // 処理対象のPコードを保持
        const selectedPCode = this.model.viewRememberGoodsList[selectRowIndex];
        this.model.selectedRememberGoods = selectedPCode;
      } else {
        this.model.selectedRememberGoods = {} as RememberGoodsInfo;
      }
    });
  }

  // 登録画面に遷移
  private gotoRegisterPage(itemCode: string, rememberGoodsId?: string): void {
    if(this.searchFlg) {
      if (rememberGoodsId) {
        this.router.navigate([REGISTER_PAGE_ID], {
          queryParams: {
            itemCode,
            rememberGoodsId,
            // 検索フラグ
            searchFlg: this.searchFlg,
            // 検索条件
            searchOrderReceptionCatalog: this.model.searchCondition['OrderReceptionCatalog'],
            searchClassCode: this.model.searchCondition['ClassCode'],
            searchPCode: this.model.searchCondition['Pcode'],
            searchDeliveryCatalog: this.model.searchCondition['DeliveryCatalog'],
            searchBuyerCode: this.model.searchCondition['BuyerCode'],
            searchGuideGoodsName: this.model.searchCondition['GuideGoodsName'],
            searchOrderNum: this.model.searchCondition['OrderNum'],
            searchItemCode: this.model.searchCondition['ItemCode'],
            searchGuideStandardName: this.model.searchCondition['GuideStandardName']
          }
        });
      } else {
        this.router.navigate([REGISTER_PAGE_ID], {
          queryParams: {
            itemCode,
            // 検索フラグ
            searchFlg: this.searchFlg,
            // 検索条件
            searchOrderReceptionCatalog: this.model.searchCondition['OrderReceptionCatalog'],
            searchClassCode: this.model.searchCondition['ClassCode'],
            searchPCode: this.model.searchCondition['Pcode'],
            searchDeliveryCatalog: this.model.searchCondition['DeliveryCatalog'],
            searchBuyerCode: this.model.searchCondition['BuyerCode'],
            searchGuideGoodsName: this.model.searchCondition['GuideGoodsName'],
            searchOrderNum: this.model.searchCondition['OrderNum'],
            searchItemCode: this.model.searchCondition['ItemCode'],
            searchGuideStandardName: this.model.searchCondition['GuideStandardName']
          }
        });
      }
    }else {
      if (rememberGoodsId) {
        this.router.navigate([REGISTER_PAGE_ID], {
          queryParams: {
            itemCode,
            rememberGoodsId,
          }
        });
      } else {
        this.router.navigate([REGISTER_PAGE_ID], {
          queryParams: {
            itemCode,
          }
        });
      }
    }
  }

  /**
   * API実行: 忘れず注文商品一覧取得
   * @memberof KYMW7400Component
   */
  private getRememberGoodsList(requestPageNo: number): void {
    // 表示リストを初期化
    this.model.viewRememberGoodsList = [] as RememberGoodsInfo[];

    // APIリクエスト
    this.getRememberGoodsListService.execute(this.model, requestPageNo, (response) => {
      const totalNum = Number(response.TotalNum);
      const countPerPage = 100; // 画面のページ単位件数
      const endPageNo = Math.ceil(totalNum / countPerPage);

      if (response.RememberGoodsList.length === 0) {
        // レスポンスの商品情報が0件、かつ、現在最初のページではない場合、前のページで再検索
        if (requestPageNo > 1) {
          this.getRememberGoodsList(requestPageNo - 1);
        }
      } else if (response.RememberGoodsList.length > 0) {
        this.model.currentPageNo = requestPageNo;
        this.model.endPageNo = endPageNo;
        this.model.totalNum = totalNum;
        this.model.startDispNum = Number(response.StartDispNum);
        this.model.endDispNum = Number(response.EndDispNum);

        // 取得データをリストに追加する
        const newList: RememberGoodsInfo[] = response.RememberGoodsList.map(_ => {
          return {
            check: false,
            rememberGoodsStatus: _.RememberGoodsStatus,
            rememberGoodsStatusName: _.RememberGoodsStatusName,
            orderNum: _.OrderNum,
            classCode: _.ClassCode,
            className: _.ClassName,
            buyerCode: _.BuyerCode,
            buyerName: _.BuyerName,
            itemCode: _.ItemCode,
            pCode: _.Pcode,
            guideGoodsName: _.GuideGoodsName,
            guideStandardName: _.GuideStandardName,
            orderReceptionStartCatalog: _.OrderReceptionStartCatalog,
            orderReceptionEndCatalog: _.OrderReceptionEndCatalog,
            deliveryStartCatalog: _.DeliveryStartCatalog,
            deliveryEndCatalog: _.DeliveryEndCatalog,
            deliveryCycleDiv: _.DeliveryCycleDiv,
            deliveryCycleDivName: _.DeliveryCycleDivName,
            goodsPrice: _.GoodsPrice,
            discountUnitPrice: _.DiscountUnitPrice,
            discountRate: _.DiscountRate,
            rememberGoodsId: _.RememberGoodsId,
            orderChangeOrderNum: _.OrderChangeOrderNum,
          };
        });

        // 一覧表示
        this.model.viewRememberGoodsList = newList;
      }
    });
  }

  /**
   * API実行: 忘れず注文商品削除
   * @memberof KYMW7400Component
   */
  private deleteRememberGoodsList(selectedItems: RememberGoodsInfo[]): void {
    // 削除対象をセット
    const deleteList = [] as DeleteRememberGoodsListParameter[];
    for (const item of selectedItems) {
      deleteList.push({
        RememberGoodsId: item.rememberGoodsId,
        OrderReceptionStartCatalog: item.orderReceptionStartCatalog,
        OrderReceptionEndCatalog: item.orderReceptionEndCatalog,
        OrderNum: item.orderNum,
        OrderChangeOrderNum: item.orderChangeOrderNum,
      });
    }

    // APIリクエスト
    this.deleteRememberGoodsListService.execute(this.model, deleteList, () => {
      this.dialogService.openAlertGetMessage('I0106', ['削除が完了']);
      // 同一ページで画面を再描画する
      this.getRememberGoodsList(this.model.currentPageNo);
    });
  }

  /**
   * API実行: 忘れず注文商品情報確定
   * @memberof KYMW7400Component
   */
  private confirmRememberGoods(): void {
    // APIリクエスト
    this.confirmRememberGoodsService.execute(this.model, () => {
      this.dialogService.openAlertGetMessage('I0106', ['情報確定が完了']);
    });
  }

}
